import {
    Component,
    DestroyRef,
    ElementRef,
    inject,
    Inject,
    OnInit,
    ViewChild
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, forkJoin, of, throwError} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, take, tap} from 'rxjs/operators';
import {TreeNode} from '@ali-hm/angular-tree-component';
import {NotificationService} from '@emm/shared/services/notification.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {Submenu} from '@emm/core/models/submenu.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {MenuService} from '@emm/core/services/menu.service';
import {MenuStoreService} from '@emm/menu/services/menu-store.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {MenuFormFacadeService} from '@emm/menu/services/menu-form-facade.service';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {getCascadeMatchPath, CascadeChip, MatchPathResult} from '@emm/core/util/cascade-matchpath.util';

interface ChipState {
    name: string;
    selected: boolean;
    tag: string;
}

@Component({
    selector: 'emm-update-cascade-item-dialog',
    styleUrls: ['./update-cascade-item-dialog.component.scss'],
    templateUrl: './update-cascade-item-dialog.component.html',
    standalone: false
})
export class UpdateCascadeItemDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    isLinear = true;
    featureScope: string;
    firstFormGroup: FormGroup = new FormGroup<any>({});
    secondFormGroup: FormGroup = new FormGroup<any>({});
    menuKey: MenuKeyContext;

    // Reactive state management
    bottomChips$ = new BehaviorSubject<ChipState[]>([]);
    matchCount$ = new BehaviorSubject<number>(0);
    spinner$ = new BehaviorSubject<boolean>(false);
    path$ = new BehaviorSubject<object | string | null>(null);
    showPath$ = new BehaviorSubject<boolean>(false);

    color = '#e5173f';
    mode = 'indeterminate';
    diameter = 25;
    response;
    subMenu;
    updates = [];
    selectedSubmenus: string[] = [];
    submenus: Submenu[];
    currentSubmenu;
    forceOverrides = this.data.forceOverrides;
    locationSet = this.data.locationSet;
    effectiveDate = this.data.effectiveDate;
    removedMessage = 'Removed';
    itemDetail: ItemDetail;

    @ViewChild('cascadeCheckbox') cascadeCheckbox: ElementRef;
    checkbox = new FormControl('');
    filterValue = '';
    filterSize: number;
    formattedOverrides = [];
    displayedColumns: string[] = [];
    forceOverridesInheritanceMap: Record<string, boolean> = {};
    dataSource = new BehaviorSubject<any[]>([]);
    newUpdateFlow$ = this.formFacade.newUpdateFlow$;
    useNewFlow = new BehaviorSubject<boolean>(false);
    isLoading = new BehaviorSubject<boolean>(true);
    useNewCascadeMatchPaths$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_NEW_CASCADE_MATCH_PATHS);
    useNewCascadeMatchPaths = false;

    // Computed observables
    selectedBottomChips$ = this.bottomChips$.pipe(
        map(chips => chips.filter(chip => chip.selected))
    );

    constructor(
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<UpdateCascadeItemDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private menuService: MenuService,
        private menuStore: MenuStoreService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private api: MenuApiService,
        private formFacade: MenuFormFacadeService,
        private launchDarklyService: LaunchDarklyService,
    ) {
        this.updates = data.updates;
        this.itemDetail = data.itemDetail;
        this.menuKey = data.menuKey;
        this.featureScope = this.data.featureScope;
    }

    ngOnInit(): void {
        this.menuStore.querySubmenus.selected$.subscribe(result => this.currentSubmenu = result);
        this.treeFacade.resetCascadeSubmenus();
        this.setSubmenus();
        this.initChips();

        this.useNewCascadeMatchPaths$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.useNewCascadeMatchPaths = value);

        this.firstFormGroup = this.formBuilder.group({
            checkbox: this.checkbox
        });
        this.secondFormGroup = this.formBuilder.group({});

        // React to chip selection changes and recalculate path
        this.bottomChips$
            .pipe(
                debounceTime(50), // Small debounce to batch updates
                distinctUntilChanged((prev, curr) =>
                    JSON.stringify(prev) === JSON.stringify(curr)
                ),
                tap(() => this.recalculatePath()),
                switchMap(() => this.findMatches(this.featureScope, this.path$.value)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.newUpdateFlow$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                if (result) {
                    this.setDisplayedColumns();
                    this.formatDataSource();
                } else {
                    this.isLoading.next(false);
                }
                this.useNewFlow.next(result);
            });

        this.refreshCascadeSubmenus();
    }

    setDisplayedColumns(): void {
        this.displayedColumns = ['attribute', 'locations', 'currentValue', 'newValue'];
        if (this.locationSet !== 'Location') {
            this.displayedColumns.push('forceOverrides');
        }
    }

    forceOverrideForAttribute(row: any): void {
        row.forceOverrides = !row?.forceOverrides;
        if (row?.forceOverrides) {
            this.forceOverridesInheritanceMap[row.attribute] = true;
        } else {
            delete this.forceOverridesInheritanceMap[row.attribute];
        }
    }

    formatDataSource(): void {
        if (!this.itemDetail?.tagPath) return;
        const updateKeys = Object.keys(this.updates).filter(key => key !== 'components');
        const tagPathArray = this.itemDetail?.tagPath?.split('/');
        tagPathArray?.shift();
        tagPathArray?.shift();
        const itemPath = tagPathArray?.join('/');
        const requests = updateKeys.map(attribute =>
            this.api.getAttributeOverrides(attribute, this.locationSet,  this.currentSubmenu, `/${itemPath}`,
                this.effectiveDate).pipe(
                    takeUntilDestroyed(this.destroyRef),
                    map(res => ({attribute, res})),
                    catchError(() => of({attribute, res: null}))
                )
        );

        forkJoin(requests).subscribe({
            next: (results) => {
                this.formattedOverrides = [];
                const tableRows = results.map(({attribute, res}) => {
                    const overrides = res
                        ? Object.entries(res.overrides).map(([key, value]) => ({key, value}))
                        : [];

                    this.formattedOverrides.push(...overrides);

                    return {
                        attribute,
                        locations: overrides.length ? overrides.map(o => o.key).join(',\n') : 'None',
                        currentValue: this.itemDetail?.attributes.find(a => a.jsonProperty === attribute)?.value,
                        newValue: this.updates[attribute],
                        forceOverrides: false
                    };
                });

                this.dataSource.next(tableRows);
                this.isLoading.next(false);
            },
            error: (err) => {
                console.error('Error loading attribute overrides:', err);
                this.notificationService.error('Failed to load attribute overrides. Please try again.');
                this.isLoading.next(false);
            }
        });
    }

    initChips(): void {
        // Find item hierarchy
        const nodes: ChipState[] = [];
        nodes.push(this.createChip(this.data?.item?.data?.data, true));
        this.findParents(this.data?.item).forEach((parent) =>
            nodes.push(this.createChip(parent?.data?.data, false))
        );
        const chips = nodes.reverse();

        // Only use bottom-level chips (parent ancestors)
        const bottomChips = chips.slice(1);
        this.bottomChips$.next(bottomChips);

        // Initial path calculation
        this.recalculatePath();
    }

    findParents(item: TreeNode): TreeNode[] {
        return this.findParent(item, []);
    }

    findParent(node: TreeNode, parents: TreeNode[]): TreeNode[] {
        const parent = node?.parent;
        if (parent && !node.isRoot) {
            parents.push(parent);
            return this.findParent(parent, parents);
        }
        return parents;
    }

    createChip({name = '', tag = ''} = {}, selected: boolean): ChipState {
        return {name, selected, tag};
    }

    toggleSelectBottomChip(chip: ChipState): void {
        // Validate deselecting bottom chip (item)
        if (chip.tag === this.data.item.data.data.tag) {
            this.notificationService.error('Cannot deselect item to be updated');
            return;
        }

        const currentChips = this.bottomChips$.value;
        const chipIndex = currentChips.findIndex(c => c.tag === chip.tag);

        if (chipIndex === -1) return;

        // Toggle selected chip
        const newValue = !chip.selected;
        const updatedChips = [...currentChips];
        updatedChips[chipIndex] = {...updatedChips[chipIndex], selected: newValue};

        // If selecting, set all child descendants to true
        if (newValue) {
            for (let i = chipIndex + 1; i < updatedChips.length; i++) {
                updatedChips[i] = {...updatedChips[i], selected: true};
            }
        }

        // Check if the deselection creates 2 or more gaps
        const selectedIndexes = updatedChips
            .map((ch, index) => (ch.selected ? index : -1))
            .filter(index => index !== -1);

        const sortedIndexes = selectedIndexes.sort((a, b) => a - b);
        const gapCount = sortedIndexes.reduce((count, index, i) => {
            if (i < sortedIndexes.length - 1 && sortedIndexes[i + 1] - index > 1) {
                return count + 1;
            }
            return count;
        }, 0);

        if (gapCount > 1) {
            this.notificationService.error('Cannot create 2 or more gaps in selected chips');
            return;
        }

        // Update state immutably
        this.bottomChips$.next(updatedChips);
    }

    private recalculatePath(): void {
        const chips = this.bottomChips$.value;
        const selectedBottomChips = chips.filter(chip => chip.selected);
        const path = this.getPath(selectedBottomChips, chips);
        this.path$.next(path);
    }

    getPath(selectedBottomChips: ChipState[], allBottomChips: ChipState[]): MatchPathResult {
        return getCascadeMatchPath(
            selectedBottomChips as CascadeChip[],
            allBottomChips as CascadeChip[],
            this.useNewCascadeMatchPaths
        );
    }

    findMatches(featureScope: string, path: any): any {
        this.spinner$.next(true);

        if (!this.menuKey?.submenu) {
            const submenu = this.getSearchSubmenu();
            if (!this.selectedSubmenus.includes(submenu) && submenu !== null) {
                this.treeFacade.selectCascadeSubmenus(submenu);
                this.refreshCascadeSubmenus();
            }
        }

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesForUpdateCascade(featureScope, this.data.effectiveDate, this.menuKey, path, this.selectedSubmenus)
                .pipe(
                    tap((results) => {
                        this.spinner$.next(false);
                        this.matchCount$.next(results);
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching matches';
                        this.notificationService.error(errorMessage);
                        this.spinner$.next(false);
                        return throwError(errorMessage);
                    })
                );

            return source$;
        }

        this.spinner$.next(false);
        return of(null);
    }

    togglePath(): void {
        this.showPath$.next(!this.showPath$.value);
    }

    onSave(): void {
        if (!this.menuKey?.submenu) {
            const submenu = this.getSearchSubmenu();
            if (!this.selectedSubmenus.includes(submenu) && submenu !== null) {
                this.treeFacade.selectCascadeSubmenus(submenu);
                this.refreshCascadeSubmenus();
            }
        }
        const checkbox = this.cascadeCheckbox ? this.cascadeCheckbox['checked'] : false;
        const path = this.path$.value;

        if (this.useNewFlow.value === false) {
            this.formFacade.updateMenuItemCascade(this.itemDetail.tagPath, path, this.updates, checkbox);
            this.dialogRef.close({path, isChecked: checkbox});
        } else {
            this.formFacade.updateMenuItemCascade(this.itemDetail.tagPath, path, this.updates, checkbox,
                this.forceOverridesInheritanceMap);
            this.dialogRef.close({path, isChecked: checkbox});
        }
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    refreshCascadeSubmenus(): void {
        this.treeFacade.selectedSubmenusUpdate$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                this.selectedSubmenus = [...result].filter(submenu => submenu !== null && submenu !== undefined);
            });
    }

    deselectCascadeSubmenus(submenuTag: string): void {
        if (this.selectedSubmenus.includes(submenuTag)) {
            this.selectedSubmenus.splice(this.selectedSubmenus.indexOf(submenuTag), 1);
        }
    }

    updateAllSelected(checked: boolean, checkedValue: string): void {
        if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
            this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
        }

        this.spinner$.next(true);

        if (checked) {
            this.treeFacade.selectCascadeSubmenus(checkedValue);
        } else {
            this.treeFacade.deselectCascadeSubmenus(checkedValue);
            this.deselectCascadeSubmenus(checkedValue);
        }

        if (!this.menuKey?.submenu) {
            const submenu = this.getSearchSubmenu();
            if (!this.selectedSubmenus.includes(submenu) && submenu !== null) {
                this.treeFacade.selectCascadeSubmenus(submenu);
            }
        }

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService.countMatchesForUpdateCascade(
                this.featureScope,
                this.data.effectiveDate,
                this.menuKey,
                this.path$.value,
                this.selectedSubmenus
            )
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (results) => {
                        this.matchCount$.next(results);
                        this.spinner$.next(false);
                    },
                    error: () => {
                        const errorMessage = 'Error searching parent nodes';
                        this.notificationService.error(errorMessage);
                        this.spinner$.next(false);
                    }
                });
        }
    }

    private setSubmenus(filterStr?: string): void {
        this.api.getSubmenusByFeatureScope(this.featureScope, this.menuKey)
            .pipe(
                debounceTime(300),
                map((result) => {
                    if (filterStr) {
                        return result.filter((item) => item.name.toLowerCase().includes(filterStr)
                            || item.id.toLowerCase().includes(filterStr));
                    } else {
                        return result;
                    }
                }),
                map((submenus) => this.submenus = submenus),
                tap((submenus) => this.filterSize = submenus.length),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                error: (err) => {
                    console.error('Error loading submenus:', err);
                    this.notificationService.error('Failed to load submenus. Please try again.');
                }
            });
    }

    onFilterChange($event: Event): void {
        const filterStr = ($event.target as HTMLInputElement).value;
        this.filterSubmenus(filterStr.toLowerCase());
    }

    getSearchSubmenu(): string {
        let submenu = null;
        this.menuStore.querySubmenus.selectedSearchSubmenu$.pipe(take(1)).subscribe(value =>
            submenu = value);
        return submenu;
    }

    private filterSubmenus(value: string): void {
        this.setSubmenus(value?.toLowerCase());
    }

    public clearFilter(): void {
        this.filterValue = '';
        this.setSubmenus();
    }
}
